import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { PremiumCard, dashboardTokens } from "./dashboardStyles";

const formatBRL = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
    : "—";

function HorizontalRanking({ items, labelKey, valueKey, max, verde }) {
  if (!items.length) {
    return (
      <Typography sx={{ fontSize: "0.82rem", color: dashboardTokens.textSecondary, py: 2 }}>
        Sem dados suficientes.
      </Typography>
    );
  }
  const top = items.slice(0, 5);
  const rest = items.length - top.length;
  return (
    <Stack spacing={1.25}>
      {top.map((item) => {
        const pct = max ? Math.round((item[valueKey] / max) * 100) : 0;
        return (
          <Box key={item[labelKey]}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#1e1e1e" }}>{item[labelKey]}</Typography>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e1e1e" }}>
                {item[valueKey]}
              </Typography>
            </Stack>
            <Box sx={{ height: 6, borderRadius: 3, backgroundColor: dashboardTokens.surfaceMuted }}>
              <Box sx={{ height: "100%", borderRadius: 3, width: `${pct}%`, backgroundColor: verde }} />
            </Box>
          </Box>
        );
      })}
      {rest > 0 && (
        <Typography sx={{ fontSize: "0.74rem", color: dashboardTokens.textSecondary }}>
          + {rest} outra{rest > 1 ? "s" : ""}
        </Typography>
      )}
    </Stack>
  );
}

function ModalitySplitBar({ porModalidade, verde, cinza, bege }) {
  const total = porModalidade.Venda + porModalidade.Aluguel + porModalidade.Temporada;
  if (!total) {
    return (
      <Typography sx={{ fontSize: "0.82rem", color: dashboardTokens.textSecondary, py: 2 }}>
        Nenhum imóvel ativo.
      </Typography>
    );
  }
  const segments = [
    { label: "Venda", value: porModalidade.Venda, color: verde },
    { label: "Aluguel", value: porModalidade.Aluguel, color: cinza },
    { label: "Temporada", value: porModalidade.Temporada, color: bege },
  ].filter((s) => s.value > 0);

  return (
    <Box>
      <Stack direction="row" sx={{ height: 10, borderRadius: 5, overflow: "hidden", mb: 1.5 }}>
        {segments.map((s) => (
          <Box key={s.label} sx={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }} />
        ))}
      </Stack>
      <Stack spacing={0.75}>
        {segments.map((s) => (
          <Stack key={s.label} direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: s.color }} />
              <Typography sx={{ fontSize: "0.82rem", color: "#1e1e1e" }}>{s.label}</Typography>
            </Stack>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1e1e1e" }}>{s.value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function PriceCard({ title, stats }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: dashboardTokens.textSecondary, mb: 0.5 }}>
        {title}
      </Typography>
      {stats.count > 0 ? (
        <>
          <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: "#1e1e1e" }}>
            {formatBRL(stats.median)}
          </Typography>
          <Typography sx={{ fontSize: "0.74rem", color: dashboardTokens.textSecondary }}>
            mediana · {stats.count} anúncios · {formatBRL(stats.min)} – {formatBRL(stats.max)}
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: "0.82rem", color: dashboardTokens.textSecondary }}>
          Sem imóveis ativos suficientes.
        </Typography>
      )}
    </Box>
  );
}

/**
 * Visão do portfólio ativo: Venda x Aluguel, tipos, cidades e mediana de
 * preço (venda e aluguel calculados separadamente — as escalas não são
 * comparáveis). Tudo derivado só de imóveis com imovelInfo.status === true.
 */
function PortfolioDistribution({ distribution, verde, cinza, bege }) {
  const maxTipo = distribution.tipoRanking[0]?.count || 0;
  const maxCidade = distribution.cidadeRanking[0]?.count || 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} lg={3}>
        <PremiumCard sx={{ p: 2, height: "100%" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1.5 }}>Venda x Aluguel</Typography>
          <ModalitySplitBar porModalidade={distribution.porModalidade} verde={verde} cinza={cinza} bege={bege} />
        </PremiumCard>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <PremiumCard sx={{ p: 2, height: "100%" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1.5 }}>Tipo de imóvel</Typography>
          <HorizontalRanking items={distribution.tipoRanking} labelKey="tipo" valueKey="count" max={maxTipo} verde={verde} />
        </PremiumCard>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <PremiumCard sx={{ p: 2, height: "100%" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1.5 }}>Cidades</Typography>
          <HorizontalRanking items={distribution.cidadeRanking} labelKey="cidade" valueKey="count" max={maxCidade} verde={verde} />
        </PremiumCard>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <PremiumCard sx={{ p: 2, height: "100%" }}>
          <Stack spacing={2}>
            <PriceCard title="Preço de venda" stats={distribution.precoVenda} />
            <PriceCard title="Preço de aluguel" stats={distribution.precoAluguel} />
          </Stack>
        </PremiumCard>
      </Grid>
    </Grid>
  );
}

export default PortfolioDistribution;
